import { COMPATIBLE, EQUIVALENT, slotSide, WEIGHT_DERIVED } from '../data/positions.ts';
import type {
  Availability,
  Player,
  PositionCode,
  PositionWeights,
  PreferredSide,
  RuleInput,
  RuleLevel,
  RuleSummary,
  SlotSpec,
  SquadStatus,
} from '../types.ts';
import { C } from './theme.ts';

/** The season the plan is drawn up for; ages are measured against it. */
export const SEASON_YEAR = 2026;

/** TFF: at most this many foreign players may sit on the first-team list. */
export const FOREIGN_LIMIT = 14;
/** TFF: foreigners beyond this count must be born on or after 01.01.2003. */
export const FOREIGN_BASE = 10;
/** TFF: the birth year that qualifies a foreigner for the "+4" allowance. */
export const YOUNG_FOREIGN_BIRTH_YEAR = 2003;
/** TFF: how many foreigners may sit above the base allowance — the "+4" itself. */
export const YOUNG_FOREIGN_ALLOWANCE = FOREIGN_LIMIT - FOREIGN_BASE;
/** UEFA: List A holds at most 25 players. */
export const LIST_A_LIMIT = 25;
/** UEFA: at least 8 of them must be homegrown. */
export const HOMEGROWN_REQUIRED = 8;
/** UEFA: at least 4 of those homegrown players must be club-trained. */
export const CLUB_TRAINED_REQUIRED = 4;
/** UEFA: List B is open to players born in this year or later. */
export const LIST_B_BIRTH_YEAR = 2005;
/** Default target depth per position. */
export const TARGET_DEPTH = 2;
/** Default annual wage budget in M€. */
export const DEFAULT_WAGE_CAP = 110;

export function age(p: { birthYear: number }): number {
  return SEASON_YEAR - p.birthYear;
}

export function isForeign(p: { turkeyEligible: boolean }): boolean {
  return !p.turkeyEligible;
}

export function isListBEligible(p: { birthYear: number; homegrown: string }): boolean {
  return p.birthYear >= LIST_B_BIRTH_YEAR && p.homegrown === 'kulup';
}

/**
 * How well a player covers a position, on the same 1–5 scale as `positionWeights`.
 * 0 means he cannot play there at all.
 *
 * A weight he was given explicitly wins outright. Failing that, a position the rules
 * treat as the same role carries its twin's weight, and one the compatibility graph
 * merely derives is worth `WEIGHT_DERIVED` — emergency cover, nothing more.
 */
export function suitability(
  p: { positionWeights: PositionWeights; excludedPositions?: PositionCode[] },
  code: PositionCode,
): number {
  const own = p.positionWeights[code] ?? 0;
  if (own > 0) return own;
  // An explicit exclusion only ever suppresses a derived position, never a weighted one.
  if (p.excludedPositions && p.excludedPositions.indexOf(code) >= 0) return 0;

  let equivalent = 0;
  for (const alt of EQUIVALENT[code] ?? []) {
    equivalent = Math.max(equivalent, p.positionWeights[alt] ?? 0);
  }
  if (equivalent > 0) return equivalent;

  for (const listed of Object.keys(p.positionWeights) as PositionCode[]) {
    if (!p.positionWeights[listed]) continue;
    if ((COMPATIBLE[listed] ?? []).indexOf(code) >= 0) return WEIGHT_DERIVED;
  }
  return 0;
}

/** True when this slot sits on the flank the player asked for. */
export function matchesPreferredSide(
  p: { preferredSide: PreferredSide },
  slot: SlotSpec,
): boolean {
  return p.preferredSide !== 'any' && slotSide(slot) === p.preferredSide;
}

/** Computes the TFF, UEFA and financial position of one list of players. */
export function summarise<T extends RuleInput>(players: T[]): RuleSummary<T> {
  const foreign = players.filter(isForeign);
  const youngForeign = foreign.filter((p) => p.birthYear >= YOUNG_FOREIGN_BIRTH_YEAR);
  const overBase = Math.max(0, foreign.length - FOREIGN_BASE);
  const tffViolation = foreign.length > FOREIGN_LIMIT || overBase > youngForeign.length;

  const listA = players.filter((p) => p.onListA);
  const homegrown = listA.filter((p) => p.homegrown === 'kulup' || p.homegrown === 'ulke');
  const clubTrained = listA.filter((p) => p.homegrown === 'kulup');
  const listB = players.filter(isListBEligible);

  const totalWage = players.reduce((sum, p) => sum + p.wage, 0);
  const totalValue = players.reduce((sum, p) => sum + p.marketValue, 0);
  const averageAge = players.length
    ? players.reduce((sum, p) => sum + age(p), 0) / players.length
    : 0;

  // Practical List A capacity: every missing homegrown player burns a slot.
  const homegrownShort = Math.max(0, HOMEGROWN_REQUIRED - homegrown.length);
  const clubTrainedShort = Math.max(0, CLUB_TRAINED_REQUIRED - clubTrained.length);
  const listACapacity = LIST_A_LIMIT - Math.max(homegrownShort, clubTrainedShort);

  return {
    foreign,
    youngForeign,
    overBase,
    tffViolation,
    listA,
    homegrown,
    clubTrained,
    listB,
    totalWage,
    totalValue,
    averageAge,
    homegrownShort,
    clubTrainedShort,
    listACapacity,
  };
}

export interface RuleLevels {
  foreign: RuleLevel;
  youngForeign: RuleLevel;
  listA: RuleLevel;
  wage: RuleLevel;
}

/**
 * Severity of each of the four rules. This is the single source of truth: the rule cards
 * and the UYGUNLUK headline figure both read it, so they cannot contradict each other.
 *
 * Note what counts as a List A violation. Falling short of the homegrown quota is not
 * illegal — it shrinks how many players may be registered, which `listACapacity` already
 * accounts for. Only exceeding that reduced capacity is a breach; a shortfall on its own
 * is a warning.
 */
export function ruleLevels(
  listRules: RuleSummary,
  squadRules: RuleSummary,
  wageCap: number,
): RuleLevels {
  return {
    foreign:
      listRules.foreign.length > FOREIGN_LIMIT
        ? 'violation'
        : listRules.foreign.length === FOREIGN_LIMIT
          ? 'warn'
          : 'ok',
    youngForeign:
      listRules.overBase > listRules.youngForeign.length
        ? 'violation'
        : listRules.overBase === listRules.youngForeign.length && listRules.overBase > 0
          ? 'warn'
          : 'ok',
    listA:
      listRules.listA.length > listRules.listACapacity
        ? 'violation'
        : listRules.homegrownShort > 0 || listRules.clubTrainedShort > 0
          ? 'warn'
          : 'ok',
    wage:
      squadRules.totalWage > wageCap
        ? 'violation'
        : squadRules.totalWage > wageCap * 0.9
          ? 'warn'
          : 'ok',
  };
}

/**
 * Regulatory standing — the worst of the TFF and UEFA rules. The wage budget is left out
 * on purpose: it is a target the user sets, not a regulation, and it has its own
 * indicator in the MAAŞ / YIL figure.
 */
export function complianceLevel(levels: RuleLevels): RuleLevel {
  const regulatory = [levels.foreign, levels.youngForeign, levels.listA];
  if (regulatory.indexOf('violation') >= 0) return 'violation';
  if (regulatory.indexOf('warn') >= 0) return 'warn';
  return 'ok';
}

/** Formats a M€ figure the Turkish way: €12,5M. */
export function money(value: number): string {
  return '€' + (Math.round(value * 10) / 10).toFixed(1).replace('.', ',') + 'M';
}

/** Fixed-decimal number with a Turkish decimal comma. */
export function decimal(value: number, digits = 1): string {
  return value.toFixed(digits).replace('.', ',');
}

/** Signed delta, e.g. "+2,4" / "-0,5". */
export function delta(value: number, digits = 1): string {
  return (value > 0 ? '+' : '') + decimal(value, digits);
}

/**
 * Builds the single source of short names: forenames for Turkish players
 * (Barış Alper, Enes Emre), surname for everyone else. If two players in the squad
 * collapse to the same short name, the full name (TUR) or the last two words is used.
 */
export function shortNamer(squad: Player[]): (p: Player) => string {
  const base = (p: Player): string => {
    const words = p.name.split(' ');
    return p.nationality === 'TUR'
      ? words.length > 1
        ? words.slice(0, -1).join(' ')
        : words[0]
      : words[words.length - 1];
  };
  return (p: Player) => {
    const candidate = base(p);
    const collisions = squad.filter((other) => base(other) === candidate).length;
    if (collisions < 2) return candidate;
    return p.nationality === 'TUR' ? p.name : p.name.split(' ').slice(-2).join(' ');
  };
}

/**
 * Uppercases a short name for the poster. Turkish players get Turkish casing
 * (i → İ), everyone else the English one (i → I).
 */
export function upperFor(p: Player, text: string): string {
  return text.toLocaleUpperCase(p.nationality === 'TUR' ? 'tr' : 'en');
}

export interface StatusMeta {
  label: string;
  availability: Availability;
  onListA: boolean;
  bg: string;
  fg: string;
  border: string;
}

/** What each status means for availability, the UEFA list and the status pill colours. */
export const STATUS_META: Record<SquadStatus, StatusMeta> = {
  A: { label: 'A Listesinde', availability: 'A', onListA: true, bg: C.ink, fg: C.bg, border: C.ink },
  B: { label: 'B Listesinde', availability: 'A', onListA: false, bg: C.bg, fg: C.ink, border: C.ink },
  D: { label: 'Liste Dışında', availability: 'D', onListA: false, bg: C.bg, fg: C.muted, border: C.line },
  KC: { label: 'Kiralanacak', availability: 'D', onListA: false, bg: C.bg, fg: C.deep, border: C.red },
  KD: { label: 'Kiralandı', availability: 'X', onListA: false, bg: C.tint, fg: C.deep, border: C.red },
  SC: { label: 'Satılacak', availability: 'D', onListA: false, bg: C.bg, fg: C.deep, border: C.red },
  SD: { label: 'Satıldı', availability: 'X', onListA: false, bg: C.tint, fg: C.deep, border: C.red },
};

/** Order the statuses appear in the dropdown. */
export const STATUS_CODES: SquadStatus[] = ['A', 'B', 'D', 'KC', 'KD', 'SC', 'SD'];

/** Sort weight of each status, used by the STATÜ column. */
export const STATUS_ORDER: Record<SquadStatus, number> = {
  A: 0,
  B: 1,
  D: 2,
  KC: 3,
  SC: 4,
  KD: 5,
  SD: 6,
};

/** Statuses that mean the player has already left — they still show, greyed out. */
export function hasLeft(p: Player): boolean {
  return p.availability === 'X';
}

/** Statuses that mean an exit is planned but has not happened yet. */
export function isPlannedExit(p: Player): boolean {
  return p.status === 'KC' || p.status === 'SC';
}
